import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from './apiClient.js';

/**
 * Integration management service
 */

/**
 * Get all integrations
 */
export async function getIntegrations() {
  return apiGet('/admin/integrations');
}

/**
 * Update integration enabled state
 * @param {string} code - Integration code
 * @param {boolean} enabled - New enabled state
 * @param {string} reason - Reason for the change (required)
 * @param {number} intentAuditEventId - Intent audit event ID (required)
 */
export async function updateIntegrationEnabled(code, enabled, reason, intentAuditEventId) {
  if (!reason || !reason.trim()) {
    throw new Error('Reason is required');
  }
  if (!intentAuditEventId) {
    throw new Error('Intent audit event ID is required');
  }

  const action = enabled ? 'enable' : 'disable';
  return apiPost(`/admin/integrations/${code}/${action}`, {
    reason: reason.trim(),
    intentAuditEventId,
  });
}

/**
 * Test integration connection
 * @param {string} code - Integration code
 * @param {string} reason - Reason for testing (required)
 * @param {number} intentAuditEventId - Intent audit event ID (optional, backend may not support yet)
 */
export async function testIntegration(code, reason, intentAuditEventId) {
  if (!reason || !reason.trim()) {
    throw new Error('Reason is required');
  }

  // Note: Backend test route may not support intentAuditEventId yet
  // If it does, include it; otherwise just send reason
  const body = { reason: reason.trim() };
  if (intentAuditEventId) {
    body.intentAuditEventId = intentAuditEventId;
  }

  return apiPost(`/admin/integrations/${code}/test`, body);
}

/**
 * Create or update API key for integration
 */
export async function createOrUpdateApiKey(integrationCode, key, secret = null) {
  return apiPost('/admin/api-keys', {
    integration_code: integrationCode,
    key,
    secret,
  });
}

/**
 * Delete API key
 */
export async function deleteApiKey(keyId) {
  return apiDelete(`/admin/api-keys/${keyId}`);
}

/**
 * Import owners from Monday.com
 * @param {string} reason - Reason for import (required)
 * @param {string} intentAuditEventId - Intent audit event ID (required)
 * @returns {Promise<object>} Import result with runId and summary
 */
export async function importMondayOwners(reason, intentAuditEventId) {
  if (!reason || !reason.trim()) {
    throw new Error('Reason is required');
  }
  if (!intentAuditEventId) {
    throw new Error('Intent audit event ID is required');
  }

  return apiPost('/admin/integrations/monday/import-owners', {
    reason: reason.trim(),
    intentAuditEventId,
  });
}

/**
 * DEV-only seed import from Monday.com Client Profile board into Owner CRM V1A
 * @param {string} reason - Reason for import (required)
 * @param {string} intentAuditEventId - Intent audit event ID (required)
 * @returns {Promise<object>} Import result with summary
 */
export async function importMondayOwnerSeed(reason, intentAuditEventId) {
  if (!reason || !reason.trim()) {
    throw new Error('Reason is required');
  }
  if (!intentAuditEventId) {
    throw new Error('Intent audit event ID is required');
  }

  return apiPost('/admin/integrations/monday/import-owner-seed', {
    reason: reason.trim(),
    intentAuditEventId,
  });
}

/**
 * Get single integration by code
 * @param {string} code - Integration code
 */
export async function getIntegration(code) {
  return apiGet(`/admin/integrations/${code}`);
}

/**
 * Update integration config
 * @param {string} code - Integration code
 * @param {object} config - Config object to merge
 * @param {string} reason - Reason for the change (required)
 * @param {number} intentAuditEventId - Intent audit event ID (required)
 */
export async function updateIntegrationConfig(code, config, reason, intentAuditEventId) {
  if (!reason || !reason.trim()) {
    throw new Error('Reason is required');
  }
  if (!intentAuditEventId) {
    throw new Error('Intent audit event ID is required');
  }
  if (!config || typeof config !== 'object') {
    throw new Error('Config must be an object');
  }

  return apiPatch(`/admin/integrations/${code}/config`, {
    config,
    reason: reason.trim(),
    intentAuditEventId,
  });
}

/**
 * Get health rules for an integration
 * @param {string} code - Integration code
 */
export async function getHealthRules(code) {
  return apiGet(`/admin/integrations/${code}/health-rules`);
}

/**
 * Update health rules for an integration
 * @param {string} code - Integration code
 * @param {object} rules - Health rules object
 * @param {boolean} rules.enabled
 * @param {number|null} rules.warn_after_minutes
 * @param {number|null} rules.error_after_minutes
 * @param {number} rules.fail_warn_count
 * @param {number} rules.fail_error_count
 * @param {string} reason - Reason for the change (required)
 * @param {number} intentAuditEventId - Intent audit event ID (required)
 */
export async function updateHealthRules(code, rules, reason, intentAuditEventId) {
  if (!reason || !reason.trim()) {
    throw new Error('Reason is required');
  }
  if (!intentAuditEventId) {
    throw new Error('Intent audit event ID is required');
  }

  return apiPatch(`/admin/integrations/${code}/health-rules`, {
    ...rules,
    reason: reason.trim(),
    intentAuditEventId,
  });
}

